/**
 * Pie Chart Visualization Component (DASH-8.2)
 *
 * Renders dynamic widget data as a pie chart using Chart.js.
 * Migrated from Recharts to Chart.js for visual consistency with legacy widgets.
 * Wrapped in React.memo for performance (DASH-8.7)
 */

import React, { useMemo, useCallback, useRef } from 'react';
import { Pie, getElementAtEvent } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
  ChartEvent,
  ActiveElement,
} from 'chart.js';
import { useTheme } from '@/contexts/ThemeContext';
import { generateChartColorPalette } from '@/utils/chartColorUtils';
import type { DynamicWidgetConfig, QueryResult } from '@/types/dashboard';
import type { VisualizationClickEvent } from './DynamicVisualization';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

interface PieChartVisualizationProps {
  data: QueryResult;
  config: DynamicWidgetConfig;
  /** Callback when a pie segment is clicked (DASH-24.2) */
  onSegmentClick?: (event: VisualizationClickEvent) => void;
}

/**
 * Pie Chart Visualization (DASH-8.2, DASH-24.2)
 * Shows distribution with groupBy, colors, and legend
 * Wrapped in React.memo for performance (DASH-8.7)
 * DASH-24.2: Added click handler for segment drill-down
 */
export const PieChartVisualization = React.memo(function PieChartVisualization({ data, config, onSegmentClick }: PieChartVisualizationProps) {
  const { currentTheme } = useTheme();
  const chartRef = useRef<ChartJS<'pie'>>(null);

  // Generate theme-based color palette
  const colors = useMemo(() => {
    return generateChartColorPalette(currentTheme.colors, 12);
  }, [currentTheme]);

  // Expect data format: [{ label, value }, ...]
  const chartData = Array.isArray(data.data) ? data.data : [];

  // Handle chart click (DASH-24.2)
  const handleChartClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      console.log('[PieChart] Click detected, chartRef:', !!chartRef.current, 'onSegmentClick:', !!onSegmentClick);
      if (!chartRef.current || !onSegmentClick) return;

      const elements = getElementAtEvent(chartRef.current, event);
      console.log('[PieChart] Elements at click:', elements.length, elements);
      if (elements.length === 0) return;

      const clickedIndex = elements[0].index;
      const clickedItem = chartData[clickedIndex];
      console.log('[PieChart] Clicked item:', clickedIndex, clickedItem);

      if (clickedItem) {
        onSegmentClick({
          value: clickedItem.id || clickedItem.label || clickedItem.name,
          index: clickedIndex,
          data: clickedItem,
        });
      }
    },
    [chartData, onSegmentClick]
  );

  // Calculate total for percentages
  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [chartData]);

  // Format percentage helper
  const formatPercentage = (value: number): string => {
    if (total === 0) return '0%';
    const percentage = (value / total) * 100;
    return `${percentage.toFixed(1)}%`;
  };

  // Prepare Chart.js data
  const chartJsData = {
    labels: chartData.map((item) => item.label || item.name || 'Unknown'),
    datasets: [
      {
        data: chartData.map((item) => item.value || 0),
        // CB-7.5/UX-DR12: honor a per-point semantic color when provided (e.g. contract
        // envelope-status accents); fall back to the theme palette otherwise.
        backgroundColor: chartData.map((item, index) => item.color || colors[index % colors.length]),
        borderColor: '#ffffff',
        borderWidth: 2,
      },
    ],
  };

  // Chart options (legend disabled - using custom legend below)
  const options: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Disable built-in legend
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.parsed;
            const percentage = formatPercentage(value);
            return `${label}: ${value} projects (${percentage})`;
          },
        },
      },
    },
  };

  return (
    <div className="flex flex-col h-full">
      {/* Pie Chart (DASH-24.2: Added click handler) */}
      <div className="flex-1 cursor-pointer" style={{ minHeight: '250px' }}>
        <Pie ref={chartRef} data={chartJsData} options={options} onClick={handleChartClick} />
      </div>

      {/* Custom Legend - Left Aligned & Scrollable */}
      <div className="mt-3 max-h-40 overflow-y-auto px-2">
        <div className="flex flex-col gap-2">
          {chartData.map((item, index) => {
            const label = item.label || item.name || 'Unknown';
            const value = item.value || 0;
            const percentage = formatPercentage(value);
            return (
              <div
                key={index}
                className="flex items-center gap-2 text-sm"
              >
                {/* Color Box (CB-7.5: per-point semantic color when provided) */}
                <div
                  className="w-4 h-4 rounded flex-shrink-0"
                  style={{ backgroundColor: item.color || colors[index % colors.length] }}
                />
                {/* Label with Count and Percentage */}
                <span className="text-left">
                  {label} ({value}, {percentage})
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});
